import { useState, useEffect } from 'react';
import { BlogPost } from '@/types/blog';
import { getAllBlogPosts, getFeaturedBlogPosts } from '@/utils/contentLoader';
import { calculateReadingTime, formatDate } from '@/utils/blogUtils';
import BlogCard from '@/components/blog/BlogCard';
import SEO from '@/components/SEO';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Sparkles, Calendar, Clock } from 'lucide-react';

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const [allPosts, featured] = await Promise.all([
          getAllBlogPosts(),
          getFeaturedBlogPosts()
        ]);
        setPosts(allPosts);
        setFeaturedPosts(featured);
      } catch (error) {
        console.error('Error loading blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;
    window.scrollTo(0, 0);
  }, []);

  // Blog listing structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Blog",
        "@id": "https://coderide.ai/blog#blog",
        "name": "CodeRide Blog",
        "description": "Insights, best practices, and updates on AI-assisted development and context management for developers.",
        "url": "https://coderide.ai/blog",
        "publisher": {
          "@type": "Organization",
          "name": "CodeRide",
          "url": "https://coderide.ai"
        },
        "inLanguage": "en-US",
        "blogPost": posts.map(post => ({
          "@type": "BlogPosting",
          "@id": `https://coderide.ai/blog/${post.slug}#blogpost`,
          "headline": post.title,
          "description": post.description,
          "url": `https://coderide.ai/blog/${post.slug}`,
          "datePublished": post.date,
          "author": {
            "@type": "Organization",
            "name": post.author
          }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://coderide.ai/blog#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://coderide.ai/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://coderide.ai/blog"
          }
        ]
      }
    ]
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen font-sans bg-background">
        <SEO 
          title="Blog - CodeRide"
          description="Insights, best practices, and updates on AI-assisted development and context management for developers."
          canonical="https://coderide.ai/blog"
        />
        <Header />
        <main className="flex-1 container mx-auto px-4 py-16">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-muted rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-muted rounded w-96 mx-auto"></div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen font-sans bg-background dark">
      <SEO 
        title="Blog - CodeRide | AI Development Insights & Best Practices"
        description="Discover insights, best practices, and updates on AI-assisted development, context management, and developer productivity with CodeRide."
        canonical="https://coderide.ai/blog"
        structuredData={structuredData}
      />
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-background to-muted/30 py-16 md:py-24 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <div 
              className="w-full h-full bg-cover bg-center bg-no-repeat opacity-65"
              style={{
                backgroundImage: `url('https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/cr_bg_2.webp?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyX2JnXzIud2VicCIsImlhdCI6MTc0NDk2MTQwNywiZXhwIjoxNzc2NDk3NDA3fQ.IfeUNJDzKofonFVKJ4W7xXhMNEFh392WSjUZ98qCVXs')`
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-b from-background/70 to-background/50"></div>
          </div>
          
          <div className="container mx-auto px-4 text-center relative z-10">
            <div className="mb-4">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-coderide-light-blue/10 text-coderide-light-blue border border-coderide-light-blue/20">
                <Sparkles className="size-4 text-[#53CADE]" />
                Vibe Coding Insights
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              CodeRide Blog
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Discover insights, best practices, and expert guidance on vibe coding, context management, and building better software with persistent AI memory.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-coderide-light-blue" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                </svg>
                <span>Expert vibe coding strategies</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-coderide-light-blue" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
                <span>Context management best practices</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-coderide-light-blue" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                <span>CodeRide product updates</span>
              </div>
            </div>
            <Button 
              variant="primary"
              showArrow={true}
              onClick={() => window.open('https://app.coderide.ai/auth/sign-up', '_blank')}
            >
              Get Started for Free
            </Button>
          </div>
        </section>

        {/* Featured Posts */}
        {featuredPosts.length > 0 && (
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold text-foreground mb-8 text-center">
                Featured {featuredPosts.length === 1 ? 'Article' : 'Articles'}
              </h2>
              
              {featuredPosts.length === 1 ? (
                // Hero layout for single featured post
                <div className="max-w-4xl mx-auto">
                  {featuredPosts.map((post) => {
                    const readingTime = calculateReadingTime(post.content);
                    return (
                      <div key={post.slug} className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-600 hover:border-coderide-light-blue/50 transition-all duration-300 hover:shadow-xl overflow-hidden">
                        {/* Hero Image */}
                        {post.image && (
                          <div className="relative h-64 md:h-80">
                            <Link to={`/blog/${post.slug}`}>
                              <img 
                                src={post.image} 
                                alt={post.imageAlt || post.title}
                                width={1200}
                                height={630}
                                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 800px, 1200px"
                                decoding="async"
                                loading="lazy"
                                className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                              />
                            </Link>
                            <div className="absolute top-4 left-4">
                              <Badge variant="secondary" className="bg-coderide-light-blue/10 text-coderide-light-blue border-coderide-light-blue/20">
                                Featured
                              </Badge>
                            </div>
                          </div>
                        )}
                        
                        {/* Content */}
                        <div className="p-8">
                          {/* Tags */}
                          {post.tags && post.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {post.tags.slice(0, 4).map((tag, index) => (
                                <Badge 
                                  key={index} 
                                  variant="outline" 
                                  className="text-xs bg-coderide-purple/10 text-coderide-purple border-coderide-purple/30 hover:bg-coderide-purple/20"
                                >
                                  {tag}
                                </Badge>
                              ))}
                              {post.tags.length > 4 && (
                                <Badge 
                                  variant="outline" 
                                  className="text-xs bg-gray-700/50 text-gray-400 border-gray-600"
                                >
                                  +{post.tags.length - 4}
                                </Badge>
                              )}
                            </div>
                          )}
                          
                          {/* Title */}
                          <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4 leading-tight hover:text-coderide-light-blue transition-colors">
                            <Link to={`/blog/${post.slug}`}>
                              {post.title}
                            </Link>
                          </h3>
                          
                          {/* Excerpt */}
                          <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                            {post.excerpt}
                          </p>
                          
                          {/* Meta and CTA */}
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex items-center gap-6 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                <span>{formatDate(post.date)}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                <span>{readingTime}</span>
                              </div>
                              <span>By {post.author}</span>
                            </div>
                            
                            <Button 
                              asChild
                              className="bg-coderide-purple hover:bg-coderide-purple/80 text-white w-fit"
                            >
                              <Link to={`/blog/${post.slug}`}>
                                Read Article
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                // Grid layout for multiple featured posts
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                  {featuredPosts.map((post) => (
                    <BlogCard key={post.slug} post={post} />
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* All Posts */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-foreground mb-8 text-center">
              All Articles
            </h2>
            
            {posts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-xl text-muted-foreground mb-4">
                  No blog posts found.
                </p>
                <p className="text-muted-foreground">
                  Check back soon for new content!
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {posts.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Join Beta CTA */}
        <section className="relative py-16 bg-gradient-to-r from-coderide-light-blue/10 to-coderide-purple/10 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img 
              src="https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/cr_bg_2.webp?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyX2JnXzIud2VicCIsImlhdCI6MTc0NDk2MTQwNywiZXhwIjoxNzc2NDk3NDA3fQ.IfeUNJDzKofonFVKJ4W7xXhMNEFh392WSjUZ98qCVXs"
              alt="CodeRide Background"
              className="w-full h-full object-cover opacity-35"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-coderide-light-blue/20 to-coderide-purple/20"></div>
          </div>
          
          <div className="container mx-auto px-4 text-center relative z-10">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Ready to Transform Your AI Development Workflow?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Join thousands of developers who are already using CodeRide to eliminate AI context reset, maintain project knowledge, and build better software faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <div className="flex items-center gap-2 text-coderide-light-blue">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Persistent AI context</span>
              </div>
              <div className="flex items-center gap-2 text-coderide-light-blue">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>No more context reset</span>
              </div>
              <div className="flex items-center gap-2 text-coderide-light-blue">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Seamless integration</span>
              </div>
            </div>
            <Button 
              variant="primary"
              size="lg"
              showArrow={true}
              onClick={() => window.open('https://app.coderide.ai/auth/sign-up', '_blank')}
            >
              Get Started Now
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              Free during beta • No credit card required
            </p>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Blog;
